import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

// Styled Components
const ChartWrapper = styled.div`
  background-color: #f9f9f9;
  width: 100%;
  height: 60vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
  position: relative;
`;

const FiltersWrapper = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  justify-content: center;
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover {
    color: #000;
  }
  svg {
    width: 18px;
    height: 18px;
  }
`;

const PurchaseChangesOverTime = ({ data }) => {
  const [purchaseData, setPurchaseData] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [chartData, setChartData] = useState(null);

  const calculateMonthlyPurchases = (row) => parseFloat(row.K_42) || 0;

  useEffect(() => {
    if (data && data.length > 0) {
      const aggregatedData = data.reduce((acc, row) => {
        const supplier = row.NazwaDostawcy || "Nieznany dostawca";
        const year = parseInt(row.Rok, 10);
        const month = parseInt(row.Miesiac, 10);
        const value = calculateMonthlyPurchases(row);

        if (!acc[supplier]) acc[supplier] = {};
        if (!acc[supplier][year]) acc[supplier][year] = Array(12).fill(0);
        acc[supplier][year][month - 1] += value;

        return acc;
      }, {});

      const years = [
        ...new Set(
          Object.values(aggregatedData)
            .flatMap((s) => Object.keys(s))
            .map((y) => parseInt(y, 10))
        ),
      ].sort((a, b) => b - a);

      setAvailableYears(years);
      if (years.length > 0) setSelectedYear(years[0]);
      setPurchaseData(aggregatedData);
    }
  }, [data]);

  useEffect(() => {
    if (selectedYear) {
      const suppliers = Object.entries(purchaseData).map(([supplier, yearlyData]) => ({
        supplier,
        total: yearlyData[selectedYear]?.reduce((sum, val) => sum + val, 0) || 0,
        monthly: yearlyData[selectedYear] || Array(12).fill(0),
      }));

      const topSuppliers = suppliers.sort((a, b) => b.total - a.total).slice(0, 10);

      const labels = [
        "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
        "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
      ];

      setChartData({
        labels,
        datasets: topSuppliers.map((s, index) => ({
          label: s.supplier,
          data: s.monthly,
          borderColor: `hsl(${(index * 35) % 360}, 65%, 45%)`,
          backgroundColor: `hsla(${(index * 35) % 360}, 65%, 45%, 0.2)`,
          fill: false,
          tension: 0.4,
          pointRadius: 4,
        })),
      });
    }
  }, [selectedYear, purchaseData]);

  if (!data || data.length === 0) return <p>Ładowanie danych...</p>;

  return (
    <ChartWrapper>
      <InfoIconWrapper data-tooltip-id="purchase-time-info">
        <Info />
      </InfoIconWrapper>
      <InfoTooltip
        id="purchase-time-info"
        place="left"
        content={
          <>
            <strong>Zmiany Zakupów</strong>
            <br />
            Pokazuje miesięczne zakupy dla 10
            <br />
            największych dostawców w wybranym roku.
          </>
        }
      />

      <FiltersWrapper>
        <div>
          <label htmlFor="year-select">Rok:</label>
          <select
            id="year-select"
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </FiltersWrapper>

      {chartData && (
        <Line
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: {
                beginAtZero: true,
                title: { display: true, text: "Zakupy (PLN)" },
                ticks: {
                  callback: (value) => Number(value).toLocaleString("pl-PL"),
                },
              },
              x: {
                title: { display: true, text: "Miesiąc" },
              },
            },
            plugins: {
              title: {
                display: true,
                text: `Zakupy top 10 dostawców wg miesięc ${selectedYear}`,
              },
              tooltip: {
                callbacks: {
                  label: (ctx) =>
                    `Zakupy: ${ctx.raw.toLocaleString("pl-PL")} PLN`,
                },
              },
              legend: {
                position: "bottom",
                labels: { boxWidth: 20 },
              },
            },
          }}
        />
      )}
    </ChartWrapper>
  );
};

export default PurchaseChangesOverTime;
